import React, { useMemo } from "react";
import { Box, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { nextStep, prevStep, resetJobCreateStepper, restoreJobCreateSnapshot, setStep } from "../../../redux/features/jobCreateSlice";

import { SASButtonStyles } from "../../ui/styles/inputStyles";
import { JOB_CREATE_VALIDATORS } from "./validators/jobCreateValidators";
import { ScopeOfWorkHandle } from "../../shared/components/features/scope-of-work/controller/scope-of-work.handle";
import { QuoteModeEnum } from "../../quotes/quote-create-stepper/enums/quote-create.enums";

interface Props {
  totalSteps: number;
  stepsConfig: any[];
  scopeOfWorkRef?: React.RefObject<ScopeOfWorkHandle>;
  handleCreateJob: () => void;
  isClientSkipped?: boolean;
  isUpdateJob?: boolean;
  blockedCreateJob?: boolean;
}

const CLIENT_STEP_KEY = "selectClient";
const SCOPE_OF_WORK_STEP_KEY = "scopeOfWork";
const SUMMARY_STEP_KEY = "summary";
const RETURN_TO_SUMMARY_KEY = "jobCreateReturnToSummary";
const SCOPE_STEP_KEY = "jobCreateScopeStep";
const EDIT_SNAPSHOT_KEY = "jobCreateEditSnapshot";

const isBasicQuoteMode = (quoteMode: string, quoteType?: string) =>
  quoteMode === QuoteModeEnum.BASIC || quoteMode === "basic" || quoteType === "basic";

const isAdvancedQuoteMode = (quoteMode: string, quoteType?: string) =>
  quoteMode === QuoteModeEnum.ADVANCED || quoteMode === "advanced" || quoteType === "advanced";

const isValidBasicScopeOfWork = (scopeOfWork: any) =>
  Array.isArray(scopeOfWork) &&
  scopeOfWork.length > 0 &&
  scopeOfWork.every((item: any) => item.mode === "locked" && item.serviceUid && item.units && Number(item.units) > 0);

export const JobNextPrev = ({ totalSteps, stepsConfig, scopeOfWorkRef, handleCreateJob, isClientSkipped, isUpdateJob = false, blockedCreateJob = false }: Props) => {
  const dispatch = useDispatch();

  const jobCreateState = useSelector((state: any) => state.jobCreateStepper);

  const { step, quoteMode, quote: quoteFromJob, client, scopeOfWork } = jobCreateState;

  const currentStepConfig = stepsConfig?.[step];

  const isEditingFromSummary = sessionStorage.getItem(RETURN_TO_SUMMARY_KEY) === "true";
  const isSummaryStep = currentStepConfig?.stepKey === SUMMARY_STEP_KEY;

  const getSummaryStepIndex = () => {
    return stepsConfig.findIndex((stepConfig) => stepConfig.stepKey === SUMMARY_STEP_KEY);
  };

  const clearSummaryEditSession = () => {
    sessionStorage.removeItem(RETURN_TO_SUMMARY_KEY);
    sessionStorage.removeItem(SCOPE_STEP_KEY);
    sessionStorage.removeItem(EDIT_SNAPSHOT_KEY);
  };

  const acceptAndReturnToSummary = () => {
    const summaryStepIndex = getSummaryStepIndex();

    clearSummaryEditSession();

    if (summaryStepIndex !== -1) {
      dispatch(setStep(summaryStepIndex));
    }
  };

  const cancelAndRestoreSummary = () => {
    const summaryStepIndex = getSummaryStepIndex();
    const snapshotRaw = sessionStorage.getItem(EDIT_SNAPSHOT_KEY);

    if (snapshotRaw) {
      try {
        const snapshot = JSON.parse(snapshotRaw);
        dispatch(restoreJobCreateSnapshot(snapshot));
      } catch (error) {
        console.error("Invalid job create snapshot:", error);
      }
    }

    clearSummaryEditSession();

    if (summaryStepIndex !== -1) {
      dispatch(setStep(summaryStepIndex));
    }
  };

  const isLastStep = step === totalSteps - 1;
  const isSubWizard = currentStepConfig?.isSubWizard ?? false;
  const isClientStep = currentStepConfig?.stepKey === CLIENT_STEP_KEY;
  const isScopeStep = currentStepConfig?.stepKey === SCOPE_OF_WORK_STEP_KEY;
  const isBasicMode = isBasicQuoteMode(quoteMode, quoteFromJob?.type);
  const isAdvancedMode = isAdvancedQuoteMode(quoteMode, quoteFromJob?.type);
  const canUseAdvancedScopeWizard = isSubWizard && isAdvancedMode && !!scopeOfWorkRef?.current;

  const getCurrentStepValidator = () => {
    const validateKey = currentStepConfig?.validate;

    if (!validateKey) return null;

    return JOB_CREATE_VALIDATORS[validateKey] ?? null;
  };

  const validateCurrentStep = (): boolean => {
    if (isSubWizard) return true;

    const validator = getCurrentStepValidator();

    if (!validator) return true;

    const result = validator(jobCreateState);

    if (result === true) return true;

    alert(result);
    return false;
  };

  const canProceed = useMemo(() => {
    if (isClientStep) {
      return Boolean(isClientSkipped || client);
    }

    if (isScopeStep && !quoteMode) {
      return false;
    }

    if (!isSubWizard) {
      const validator = getCurrentStepValidator();

      return validator ? validator(jobCreateState) === true : true;
    }

    if (isBasicMode) {
      return isValidBasicScopeOfWork(scopeOfWork);
    }

    if (isAdvancedMode && scopeOfWorkRef?.current) {
      return scopeOfWorkRef.current.isValid?.() ?? false;
    }

    return false;
  }, [
    isClientStep,
    isScopeStep,
    isClientSkipped,
    client,
    quoteMode,
    isSubWizard,
    isBasicMode,
    isAdvancedMode,
    scopeOfWork,
    scopeOfWorkRef,
    currentStepConfig?.validate,
    jobCreateState,
  ]);

  const goToPreviousStep = () => {
    if (step === 0) {
      dispatch(resetJobCreateStepper());
      return;
    }

    dispatch(prevStep());
  };

  const goToNextStep = () => {
    const shouldReturnToSummary = sessionStorage.getItem("jobCreateReturnToSummary") === "true";

    if (shouldReturnToSummary && currentStepConfig?.stepKey !== SUMMARY_STEP_KEY) {
      acceptAndReturnToSummary();
      return;
    }

    if (!shouldReturnToSummary) {
      sessionStorage.removeItem(RETURN_TO_SUMMARY_KEY);
    }

    if (isLastStep) {
      handleCreateJob();
      return;
    }

    dispatch(nextStep());
  };

  const returnToSummary = () => {
    const summaryStepIndex = stepsConfig.findIndex((stepConfig) => stepConfig.stepKey === "summary");

    sessionStorage.removeItem("jobCreateReturnToSummary");
    sessionStorage.removeItem("jobCreateScopeStep");

    if (summaryStepIndex !== -1) {
      dispatch(setStep(summaryStepIndex));
    }
  };

  const handleBack = () => {
    if (isEditingFromSummary && !isSummaryStep) {
      cancelAndRestoreSummary();
      return;
    }

    if (canUseAdvancedScopeWizard) {
      const movedInsideWizard = scopeOfWorkRef.current?.prev?.() ?? false;

      if (!movedInsideWizard) {
        goToPreviousStep();
      }

      return;
    }

    goToPreviousStep();
  };

  const handleAdvancedScopeNext = () => {
    const shouldReturnToSummary = sessionStorage.getItem("jobCreateReturnToSummary") === "true";

    if (shouldReturnToSummary) {
      const isComplete = scopeOfWorkRef?.current?.isValid?.() ?? false;

      if (!isComplete) {
        alert("Please complete all required fields");
        return;
      }

      const summaryStepIndex = stepsConfig.findIndex((stepConfig) => stepConfig.stepKey === "summary");

      if (summaryStepIndex !== -1) {
        acceptAndReturnToSummary();
        return;
      }
    }

    const finished = scopeOfWorkRef?.current?.next?.() ?? false;

    if (!finished) return;

    const isComplete = scopeOfWorkRef?.current?.isValid?.() ?? false;

    if (!isComplete) {
      alert("Please complete all required fields");
      return;
    }

    goToNextStep();
  };

  const handleBasicScopeNext = () => {
    if (!isValidBasicScopeOfWork(scopeOfWork)) {
      alert("Please complete all services");
      return;
    }

    goToNextStep();
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;

    if (canUseAdvancedScopeWizard) {
      handleAdvancedScopeNext();
      return;
    }

    if (isSubWizard && isBasicMode) {
      handleBasicScopeNext();
      return;
    }

    goToNextStep();
  };

  const isBackDisabled = step === 0 && !canUseAdvancedScopeWizard;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
      <Button sx={SASButtonStyles} onClick={handleBack} disabled={isBackDisabled}>
        {isEditingFromSummary && !isSummaryStep ? "Cancel" : "Back"}
      </Button>

      <Button sx={SASButtonStyles} variant="contained" onClick={handleNext} disabled={!canProceed || (isLastStep && blockedCreateJob)}>
        {isEditingFromSummary && !isSummaryStep ? "Accept" : isLastStep ? (isUpdateJob ? "Update Job" : "Create Job") : "Next"}
      </Button>
    </Box>
  );
};
